"use client";

import { useState } from "react";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsSection,
  Switch,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import { modelsConfig, type ModelsConfigResponse } from "@excellent/ui/api/contracts/models-config";

// The copilot dock provider (docs/models/PRD.md §14, M4). Off by default: the
// master switch (`agents.copilot.provider`) must be ON and a non-claude
// selection saved before a chat turn rides the excellent-harness host.
// Operate-mode only — develop stays on the frontier claude path (§9).

export function CopilotSection({
  data,
  onChange,
}: {
  data: ModelsConfigResponse;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState(data.copilot?.provider ?? "");
  const [model, setModel] = useState(data.copilot?.model ?? "");
  const [notice, setNotice] = useState<string | null>(null);

  async function save(body: { provider?: string | null; model?: string; enableBranch?: boolean }) {
    setBusy(true);
    setNotice(null);
    try {
      await apiCall(modelsConfig.setCopilot, { body });
      onChange();
    } catch {
      setNotice("Could not save the copilot selection.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsSection
      title="Chat model"
      description="Run dock-Chat operate turns on a chosen provider through the excellent-harness host (same stream, same gate, distinct harness identity). Develop mode stays on frontier Claude. Off by default."
    >
      <div className="flex items-center gap-3 border-b border-border/40 pb-3">
        <div className="flex-1">
          <div className="type-body text-foreground">Enable the copilot provider branch</div>
          <p className="type-subtext text-muted-foreground">
            The selection below is inert until this is on; turning it off restores the Claude path
            byte-for-byte.
          </p>
        </div>
        <Switch
          checked={data.copilotBranchEnabled}
          disabled={busy}
          onCheckedChange={(on) => void save({ enableBranch: on })}
        />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Select value={provider} onValueChange={setProvider}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Provider" />
          </SelectTrigger>
          <SelectContent>
            {data.providers.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="model id (e.g. qwen2.5-coder:7b)"
          className="w-64"
        />
        <Button
          variant="outline"
          disabled={busy || !provider || !model.trim()}
          onClick={() => void save({ provider, model: model.trim() })}
        >
          {busy ? "Saving…" : "Save selection"}
        </Button>
        {data.copilot ? (
          <Button variant="ghost" disabled={busy} onClick={() => void save({ provider: null })}>
            Clear
          </Button>
        ) : null}
      </div>
      {notice ? <p className="mt-2 type-subtext text-muted-foreground">{notice}</p> : null}
    </SettingsSection>
  );
}
